/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, Pencil1Icon, Text, Title, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { ClientRow } from '../../context/ClientManagementContext';
import ClientAvatar from '../ClientAvatar';
import { getClientAvatarAccentColor } from '../../utils/avatar-accent';
import { getTypeLabels } from '../ClientProfilePanel/preference-label-maps';
import { clientRoleLabelKey } from '../../utils/client-roles';
import { useClientResponsible } from '../../hooks/useClientResponsible';
import { AttendanceActions, CLIENT_SECONDARY_ACTION_CLASS } from '../AttendanceActions';

export interface ClientDetailHeaderProps {
  client: ClientRow;
  agencyId: string;
  onNavigateToList: () => void;
  onEdit: () => void;
  onCreateDeal: () => void;
  onViewAttendance: (dealId: string) => void;
}

/**
 * Back-navigation breadcrumb, Clients / current client.
 *
 * @param props - The client's name and the list callback.
 * @returns The rendered breadcrumb row.
 */
function Breadcrumb({ clientName, onNavigateToList }: {
  clientName: string;
  onNavigateToList: () => void;
}) {
  const { t } = useTranslation('features/clients');
  return (
    <div className="flex items-center gap-(--space-2) text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
      <UnstyledButton
        type="button"
        data-testid="client-detail-breadcrumb-clients"
        onClick={onNavigateToList}
        /* 44px of target on a phone, pulled back with negative margin so the
           label stays on the crumb's own line. The detail screen hides the
           bottom navigation, so this is the only way back to the list. */
        className="flex items-center gap-(--space-1) border-none bg-transparent p-0 text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) cursor-pointer max-md:-my-(--space-3) max-md:min-h-11 max-md:-ml-(--space-2) max-md:pl-(--space-2) max-md:pr-(--space-1)"
      >
        <ChevronLeftIcon width={14} height={14} />
        {t('detail.breadcrumb')}
      </UnstyledButton>
      <Text component="span">/</Text>
      <Text component="span" className="truncate">{clientName}</Text>
    </div>
  );
}

/** One of the header's state chips. */
function Chip({ label }: { label: string }) {
  return (
    <Text
      component="span"
      className="inline-flex h-[22px] items-center whitespace-nowrap rounded-(--radius-full) bg-(--surface-tertiary) px-[9px] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--text-tertiary)"
    >
      {label}
    </Text>
  );
}

/**
 * The chips beside the client's name: their status, their roles (seeker,
 * owner, investor...) and whether they are a person or a company. What
 * they want to buy or rent lives in the Preferências card.
 *
 * @param client - The client being shown.
 * @returns The chip labels, skipping anything the row does not state.
 */
function useHeaderChips(client: ClientRow): string[] {
  const { t } = useTranslation('features/clients');
  const typeLabels = getTypeLabels();
  const roles = (client.client_roles ?? []).map((role) => t(clientRoleLabelKey(role), { defaultValue: role }));

  return [
    client.status ? t(`status.${client.status}`, { defaultValue: client.status }) : '',
    ...roles,
    typeLabels[client.type_of_client] ?? client.type_of_client,
  ].filter(Boolean) as string[];
}

/**
 * The line under the client's name: how long they have been on the base
 * and who is handling them.
 *
 * @param props - The client and the agency it belongs to.
 * @returns The context line, or null when neither fact is known.
 */
function ContextLine({ client, agencyId }: { client: ClientRow; agencyId: string }) {
  const { t } = useTranslation('features/clients');
  const responsible = useClientResponsible(agencyId, client.client_id, client.primary_broker);
  const since = client.registration_date
    ? t('detail.header.clientSince', { date: formatLocalizedDate(client.registration_date) })
    : '';
  // Whoever is handling them right now is the more perishable fact, so it
  // wins the line; the record's own responsible broker answers when no
  // attendance is open.
  const by = responsible.attendingName
    ? t('detail.header.handledBy', { name: responsible.attendingName })
    : (responsible.ownerName ? t('detail.header.responsible', { name: responsible.ownerName }) : '');
  const parts = [since, by].filter(Boolean);
  if (parts.length === 0) return null;

  return (
    <Text
      component="span"
      className="text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-(--text-secondary)"
    >
      {parts.join(' · ')}
    </Text>
  );
}

/**
 * Client detail header: the breadcrumb, then the identity block with the
 * avatar centred against the name, the state chips, the context line, and
 * the actions on the right.
 *
 * @param props - The client, the agency, and the navigation callbacks.
 * @returns The rendered header.
 */
export function ClientDetailHeader({
  client,
  agencyId,
  onNavigateToList,
  onEdit,
  onCreateDeal,
  onViewAttendance,
}: ClientDetailHeaderProps) {
  const { t } = useTranslation('features/clients');
  const chips = useHeaderChips(client);

  return (
    <div className="flex flex-col gap-(--space-4)">
      <Breadcrumb clientName={client.full_name} onNavigateToList={onNavigateToList} />

      <div
        data-testid="client-detail-header"
        className="flex flex-wrap items-start gap-(--space-4)"
      >
        {/* Centred against the whole name block rather than top-aligned:
            at 56px the avatar is taller than the chip row it would
            otherwise line up with, and it read as hanging off the top. */}
        <div className="flex-none self-center">
          <ClientAvatar
            fullName={client.full_name}
            size={56}
            accentColor={getClientAvatarAccentColor(client.full_name)}
          />
        </div>

        <div className="flex min-w-0 flex-[1_1_20rem] flex-col gap-(--space-1-5)">
          {chips.length > 0 && (
            <div className="flex flex-wrap items-center gap-(--space-2)">
              {chips.map((label) => <Chip key={label} label={label} />)}
            </div>
          )}
          <Title
            order={1}
            style={{ fontSize: 'var(--text-app-title)', fontWeight: 'var(--font-bold)' }}
            className="m-0 leading-[var(--leading-tight)] tracking-[var(--tracking-app-title)] text-(--text-primary)"
          >
            {client.full_name}
          </Title>
          <ContextLine client={client} agencyId={agencyId} />
        </div>

        {/*
          * `min-w-0` so the wrap can actually happen.
          *
          * The row was `flex-none`, which is a promise never to shrink, and
          * `flex-wrap` only wraps once a narrower width is imposed. On a
          * 391px column the three buttons measured 483px, the row refused
          * the 391, and the whole screen gained 112px of sideways scroll —
          * the reader arrived at a page already shifted, with the name cut
          * off at the left. Shrinking lets the buttons drop to a second
          * line, which is what the `flex-wrap` was there for.
          */}
        <div className="flex min-w-0 flex-wrap items-center gap-(--space-2)">
          <AttendanceActions
            client={client}
            agencyId={agencyId}
            onCreateDeal={() => onCreateDeal()}
            onViewAttendance={onViewAttendance}
          />
          <UnstyledButton
            data-testid="client-detail-edit"
            onClick={onEdit}
            className={CLIENT_SECONDARY_ACTION_CLASS}
          >
            <Pencil1Icon width={14} height={14} />
            {t('profile.edit')}
          </UnstyledButton>
        </div>
      </div>
    </div>
  );
}
